import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Shield, Lock, Key, Server, FileCheck, Globe, Download, Eye, UserCheck, Database, Wifi, AlertTriangle, CheckCircle2, Award, ExternalLink, Star, Building2, Upload, Plus, X, Trash2, Calendar, ScanLine, ShieldCheck } from "lucide-react";
import { API } from "../config";

const LOGO_URL = "/api/manuals/uploads/telcavoip_logo_white.png";

const Section = ({ icon: Icon, title, children, color = "text-blue-400", bg = "bg-blue-500/10" }) => (
  <div className="bg-[#111] border border-gray-800 rounded-2xl overflow-hidden" data-testid={`security-section-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="px-6 py-4 border-b border-gray-800 bg-[#0D0D0D] flex items-center gap-3">
      <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center`}>
        <Icon size={18} className={color} />
      </div>
      <h2 className="text-lg font-bold text-white">{title}</h2>
    </div>
    <div className="px-6 py-5 text-gray-300 text-sm leading-relaxed space-y-3">{children}</div>
  </div>
);

const Bullet = ({ children }) => (
  <div className="flex items-start gap-2">
    <CheckCircle2 size={16} className="text-lojack-blue-400 flex-shrink-0 mt-0.5" />
    <span>{children}</span>
  </div>
);

const CertCard = ({ title, issuer, code, url }) => (
  <a href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 bg-[#0D0D0D] border border-gray-700 rounded-xl p-4 hover:border-lojack-clause-500/30 hover:bg-lojack-clause-500/5 transition-colors group" data-testid={`cert-${code}`}>
    <div className="w-10 h-10 rounded-lg bg-lojack-clause-500/10 flex items-center justify-center flex-shrink-0">
      <Award size={20} className="text-lojack-clause-400" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-white font-medium text-sm">{title}</p>
      <p className="text-gray-500 text-xs mt-0.5">{issuer}</p>
      <p className="text-gray-600 text-[10px] font-mono mt-0.5">ID: {code}</p>
    </div>
    <ExternalLink size={14} className="text-gray-600 group-hover:text-lojack-clause-400 flex-shrink-0 transition-colors" />
  </a>
);

const SecurityCompliancePage = () => {
  const currentDate = new Date().toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' });
  const [assessments, setAssessments] = useState([]);
  const [loadingVA, setLoadingVA] = useState(true);
  const [showAddVA, setShowAddVA] = useState(false);
  const [vaForm, setVAForm] = useState({ scan_date: '', performed_by: '', status: 'passed', critical: 0, high: 0, medium: 0, low: 0, notes: '' });
  const [vaFile, setVAFile] = useState(null);
  const [submittingVA, setSubmittingVA] = useState(false);

  const fetchAssessments = async () => {
    try {
      const res = await axios.get(`${API}/security/vulnerability-assessments`);
      setAssessments(res.data);
    } catch { /* */ }
    finally { setLoadingVA(false); }
  };

  useEffect(() => { fetchAssessments(); }, []);

  const handleAddVA = async (e) => {
    e.preventDefault();
    setSubmittingVA(true);
    try {
      const fd = new FormData();
      Object.entries(vaForm).forEach(([k, v]) => fd.append(k, v));
      if (vaFile) fd.append('report_file', vaFile);
      await axios.post(`${API}/security/vulnerability-assessments`, fd);
      toast.success('Vulnerability Assessment registrato');
      setShowAddVA(false);
      setVAForm({ scan_date: '', performed_by: '', status: 'passed', critical: 0, high: 0, medium: 0, low: 0, notes: '' });
      setVAFile(null);
      fetchAssessments();
    } catch { toast.error('Errore durante il salvataggio'); }
    finally { setSubmittingVA(false); }
  };

  const deleteVA = async (id) => {
    if (!window.confirm('Eliminare questo assessment?')) return;
    try {
      await axios.delete(`${API}/security/vulnerability-assessments/${id}`);
      toast.success('Assessment eliminato');
      fetchAssessments();
    } catch { toast.error('Errore'); }
  };

  const latest = assessments.length > 0 ? assessments[0] : null;
  const statusColor = latest?.status === 'passed' ? 'text-lojack-blue-400' : latest?.status === 'warning' ? 'text-lojack-clause-400' : 'text-red-400';
  const statusBg = latest?.status === 'passed' ? 'bg-lojack-blue-500/10 border-lojack-blue-500/30' : latest?.status === 'warning' ? 'bg-lojack-clause-500/10 border-lojack-clause-500/30' : 'bg-red-500/10 border-red-500/30';
  const statusLabel = latest?.status === 'passed' ? 'Superato' : latest?.status === 'warning' ? 'Con Avvisi' : 'Criticita Rilevate';

  const [pdfLangOpen, setPdfLangOpen] = useState(false);
  const [pdfLang, setPdfLang] = useState("IT");
  const [translating, setTranslating] = useState(false);

  const PDF_LANGS = [
    { code: "IT", label: "Italiano", flag: "🇮🇹" }, { code: "EN", label: "English", flag: "🇬🇧" },
    { code: "FR", label: "Francais", flag: "🇫🇷" }, { code: "ES", label: "Espanol", flag: "🇪🇸" },
    { code: "DE", label: "Deutsch", flag: "🇩🇪" }, { code: "PT", label: "Portugues", flag: "🇵🇹" },
    { code: "NL", label: "Nederlands", flag: "🇳🇱" }, { code: "PL", label: "Polski", flag: "🇵🇱" },
    { code: "SE", label: "Svenska", flag: "🇸🇪" }, { code: "NO", label: "Norsk", flag: "🇳🇴" },
    { code: "FI", label: "Suomi", flag: "🇫🇮" }, { code: "DK", label: "Dansk", flag: "🇩🇰" },
    { code: "CZ", label: "Cestina", flag: "🇨🇿" }, { code: "GR", label: "Ellinika", flag: "🇬🇷" },
    { code: "CH", label: "Deutsch (CH)", flag: "🇨🇭" }, { code: "BE", label: "Francais (BE)", flag: "🇧🇪" },
    { code: "IE", label: "English (IE)", flag: "🇮🇪" }, { code: "UK", label: "English (UK)", flag: "🇬🇧" },
  ];

  const handleDownloadPdf = async () => {
    setTranslating(true);
    setPdfLangOpen(false);
    try {
      const response = await axios.get(`${API}/security/report-pdf?lang=${pdfLang}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `TelcaVoIP_Security_${pdfLang}_${new Date().toISOString().slice(0,7)}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      if (pdfLang !== 'IT') toast.success(`PDF generato in ${PDF_LANGS.find(l => l.code === pdfLang)?.label || pdfLang}`);
    } catch { toast.error("Errore nella generazione del PDF"); }
    finally { setTranslating(false); }
  };

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto animate-fade-in" data-testid="security-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#0a1628] via-[#111] to-[#0a1628] border border-gray-700 rounded-2xl p-6 sm:p-10 mb-8 text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, #1e40af 0%, transparent 60%), radial-gradient(circle at 80% 50%, #84cc16 0%, transparent 60%)' }} />
        <div className="relative z-10">
          <img src={LOGO_URL} alt="TelcaVoIP Cyber Security" className="w-28 sm:w-36 mx-auto mb-6" data-testid="security-logo" />
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">Security & Compliance Overview</h1>
          <p className="text-gray-400 text-sm sm:text-base">LoJack Digital Wallet Platform — Documentazione di Sicurezza</p>
          <p className="text-gray-500 text-xs mt-3">Documento riservato | Rev. 1.0 | {currentDate}</p>
        </div>
      </div>

      {/* Download PDF with Language Selector */}
      <div className="flex justify-end mb-6 relative" data-testid="pdf-download-area">
        <div className="flex items-center gap-2">
          <div className="relative">
            <button onClick={() => setPdfLangOpen(!pdfLangOpen)}
              className="flex items-center gap-2 px-3 py-2 bg-[#111] border border-gray-700 rounded-lg text-gray-300 text-sm hover:border-gray-500 transition-colors"
              data-testid="pdf-lang-selector">
              <span>{PDF_LANGS.find(l => l.code === pdfLang)?.flag}</span>
              <span>{PDF_LANGS.find(l => l.code === pdfLang)?.label}</span>
              <ScanLine size={12} className="text-gray-500" />
            </button>
            {pdfLangOpen && (
              <div className="absolute right-0 top-full mt-1 bg-[#1A1A1A] border border-gray-700 rounded-xl shadow-2xl z-50 w-56 max-h-80 overflow-y-auto" data-testid="pdf-lang-dropdown">
                {PDF_LANGS.map(l => (
                  <button key={l.code} onClick={() => { setPdfLang(l.code); setPdfLangOpen(false); }}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-800 transition-colors ${pdfLang === l.code ? 'text-white bg-gray-800' : 'text-gray-400'}`}
                    data-testid={`pdf-lang-${l.code}`}>
                    <span>{l.flag}</span>
                    <span>{l.label}</span>
                    {pdfLang === l.code && <span className="ml-auto text-lojack-red text-xs">&#10003;</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button onClick={handleDownloadPdf} disabled={translating}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600/10 border border-blue-600/30 rounded-lg text-blue-400 text-sm font-medium hover:bg-blue-600/20 transition-colors disabled:opacity-50"
            data-testid="download-security-pdf">
            {translating ? <div className="spinner-sm" /> : <Download size={16} />}
            {translating ? (pdfLang === 'IT' ? 'Generazione...' : 'Traduzione AI...') : 'Scarica PDF'}
          </button>
        </div>
      </div>

      {/* Vulnerability Assessment Widget */}
      <div className="mb-8" data-testid="va-widget">
        <div className="bg-[#111] border border-gray-800 rounded-2xl overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-800 bg-[#0D0D0D] flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-lojack-blue-500/10 flex items-center justify-center">
                <ScanLine size={18} className="text-lojack-blue-400" />
              </div>
              <h2 className="text-lg font-bold text-white">Vulnerability Assessment</h2>
            </div>
            <button onClick={() => setShowAddVA(true)} className="flex items-center gap-2 px-3 py-2 bg-lojack-blue-500/10 border border-lojack-blue-500/30 rounded-lg text-lojack-blue-400 text-sm font-medium hover:bg-lojack-blue-500/20 transition-colors" data-testid="add-va-btn">
              <Plus size={16} /> Registra Scansione
            </button>
          </div>

          <div className="px-6 py-5">
            {loadingVA ? (
              <div className="flex justify-center py-4"><div className="spinner" /></div>
            ) : latest ? (
              <div className="space-y-4">
                {/* Status Banner */}
                <div className={`flex items-center justify-between p-4 rounded-xl border ${statusBg}`}>
                  <div className="flex items-center gap-3">
                    <CheckCircle2 size={24} className={statusColor} />
                    <div>
                      <p className="text-white font-medium">Ultima Scansione: <span className={statusColor}>{statusLabel}</span></p>
                      <p className="text-gray-500 text-xs">{new Date(latest.scan_date).toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' })} — Eseguita da: {latest.performed_by}</p>
                    </div>
                  </div>
                  {latest.report_url && (
                    <a href={latest.report_url} target="_blank" rel="noreferrer" className="flex items-center gap-2 px-3 py-2 bg-white/5 border border-gray-700 rounded-lg text-gray-300 text-sm hover:bg-white/10 transition-colors" data-testid="download-va-report">
                      <Download size={14} /> Report
                    </a>
                  )}
                </div>

                {/* Vulnerability Counts */}
                <div className="grid grid-cols-4 gap-3">
                  <div className="bg-[#0D0D0D] border border-gray-800 rounded-xl p-3 text-center">
                    <p className={`text-2xl font-bold ${latest.critical > 0 ? 'text-red-500' : 'text-gray-600'}`}>{latest.critical}</p>
                    <p className="text-gray-500 text-xs">Critiche</p>
                  </div>
                  <div className="bg-[#0D0D0D] border border-gray-800 rounded-xl p-3 text-center">
                    <p className={`text-2xl font-bold ${latest.high > 0 ? 'text-orange-400' : 'text-gray-600'}`}>{latest.high}</p>
                    <p className="text-gray-500 text-xs">Alte</p>
                  </div>
                  <div className="bg-[#0D0D0D] border border-gray-800 rounded-xl p-3 text-center">
                    <p className={`text-2xl font-bold ${latest.medium > 0 ? 'text-lojack-clause-400' : 'text-gray-600'}`}>{latest.medium}</p>
                    <p className="text-gray-500 text-xs">Medie</p>
                  </div>
                  <div className="bg-[#0D0D0D] border border-gray-800 rounded-xl p-3 text-center">
                    <p className={`text-2xl font-bold ${latest.low > 0 ? 'text-blue-400' : 'text-gray-600'}`}>{latest.low}</p>
                    <p className="text-gray-500 text-xs">Basse</p>
                  </div>
                </div>

                {latest.notes && (
                  <p className="text-gray-400 text-sm bg-[#0D0D0D] border border-gray-800 rounded-xl p-3">{latest.notes}</p>
                )}

                {/* History */}
                {assessments.length > 1 && (
                  <div>
                    <p className="text-gray-500 text-xs uppercase tracking-wider mb-2 mt-2">Storico Scansioni</p>
                    <div className="space-y-2">
                      {assessments.slice(1).map(a => (
                        <div key={a.id} className="flex items-center justify-between bg-[#0D0D0D] border border-gray-800 rounded-xl px-4 py-2.5" data-testid={`va-history-${a.id}`}>
                          <div className="flex items-center gap-3">
                            <Calendar size={14} className="text-gray-600" />
                            <span className="text-gray-300 text-sm">{new Date(a.scan_date).toLocaleDateString('it-IT')}</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${a.status === 'passed' ? 'bg-lojack-blue-500/20 text-lojack-blue-400' : a.status === 'warning' ? 'bg-lojack-clause-500/20 text-lojack-clause-400' : 'bg-red-500/20 text-red-400'}`}>
                              {a.status === 'passed' ? 'Superato' : a.status === 'warning' ? 'Avvisi' : 'Critico'}
                            </span>
                            <span className="text-gray-600 text-xs">{a.performed_by}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {a.report_url && <a href={a.report_url} target="_blank" rel="noreferrer" className="text-gray-500 hover:text-white"><Download size={14} /></a>}
                            <button onClick={() => deleteVA(a.id)} className="text-gray-600 hover:text-red-400"><Trash2 size={14} /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-6">
                <ScanLine size={36} className="mx-auto mb-3 text-gray-700" />
                <p className="text-gray-500 text-sm">Nessuna scansione registrata</p>
                <p className="text-gray-600 text-xs mt-1">Clicca "Registra Scansione" per inserire il primo vulnerability assessment</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add VA Modal */}
      {showAddVA && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[200] flex items-center justify-center p-4" style={{width:'100vw',height:'100vh',top:0,left:0}} onClick={() => setShowAddVA(false)}>
          <div className="bg-[#1A1A1A] rounded-2xl w-full max-w-lg border border-gray-700 overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-gray-700">
              <h2 className="text-lg font-bold text-white flex items-center gap-2"><ScanLine size={20} className="text-lojack-blue-400" /> Registra Vulnerability Assessment</h2>
              <button onClick={() => setShowAddVA(false)} className="p-2 hover:bg-gray-800 rounded-lg"><X size={20} className="text-gray-400" /></button>
            </div>
            <form onSubmit={handleAddVA} className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-gray-400 text-xs">Data Scansione *</label>
                  <input type="date" value={vaForm.scan_date} onChange={e => setVAForm({...vaForm, scan_date: e.target.value})} className="w-full mt-1 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-blue-500 focus:outline-none" required data-testid="va-form-date" />
                </div>
                <div>
                  <label className="text-gray-400 text-xs">Eseguita da *</label>
                  <input value={vaForm.performed_by} onChange={e => setVAForm({...vaForm, performed_by: e.target.value})} placeholder="es. Dip. IT LoJack" className="w-full mt-1 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-blue-500 focus:outline-none" required data-testid="va-form-by" />
                </div>
              </div>
              <div>
                <label className="text-gray-400 text-xs">Esito *</label>
                <select value={vaForm.status} onChange={e => setVAForm({...vaForm, status: e.target.value})} className="w-full mt-1 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-blue-500 focus:outline-none" data-testid="va-form-status">
                  <option value="passed">Superato - Nessuna criticita</option>
                  <option value="warning">Superato con avvisi</option>
                  <option value="failed">Criticita rilevate</option>
                </select>
              </div>
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <label className="text-gray-400 text-xs">Critiche</label>
                  <input type="number" min="0" value={vaForm.critical} onChange={e => setVAForm({...vaForm, critical: parseInt(e.target.value) || 0})} className="w-full mt-1 px-3 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white text-center focus:border-red-500 focus:outline-none" data-testid="va-form-critical" />
                </div>
                <div>
                  <label className="text-gray-400 text-xs">Alte</label>
                  <input type="number" min="0" value={vaForm.high} onChange={e => setVAForm({...vaForm, high: parseInt(e.target.value) || 0})} className="w-full mt-1 px-3 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white text-center focus:border-orange-500 focus:outline-none" data-testid="va-form-high" />
                </div>
                <div>
                  <label className="text-gray-400 text-xs">Medie</label>
                  <input type="number" min="0" value={vaForm.medium} onChange={e => setVAForm({...vaForm, medium: parseInt(e.target.value) || 0})} className="w-full mt-1 px-3 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white text-center focus:border-lojack-clause-500 focus:outline-none" data-testid="va-form-medium" />
                </div>
                <div>
                  <label className="text-gray-400 text-xs">Basse</label>
                  <input type="number" min="0" value={vaForm.low} onChange={e => setVAForm({...vaForm, low: parseInt(e.target.value) || 0})} className="w-full mt-1 px-3 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white text-center focus:border-blue-500 focus:outline-none" data-testid="va-form-low" />
                </div>
              </div>
              <div>
                <label className="text-gray-400 text-xs">Note</label>
                <textarea value={vaForm.notes} onChange={e => setVAForm({...vaForm, notes: e.target.value})} rows={2} placeholder="Osservazioni, raccomandazioni..." className="w-full mt-1 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-blue-500 focus:outline-none resize-none" data-testid="va-form-notes" />
              </div>
              <div>
                <label className="text-gray-400 text-xs">Report PDF (opzionale)</label>
                <div className="mt-1 flex items-center gap-3">
                  <label className="flex items-center gap-2 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-gray-400 text-sm cursor-pointer hover:border-lojack-blue-500 transition-colors flex-1">
                    <Upload size={16} />
                    <span>{vaFile ? vaFile.name : 'Seleziona file...'}</span>
                    <input type="file" accept=".pdf,.doc,.docx" onChange={e => setVAFile(e.target.files[0])} className="hidden" data-testid="va-form-file" />
                  </label>
                  {vaFile && <button type="button" onClick={() => setVAFile(null)} className="text-gray-500 hover:text-red-400"><X size={16} /></button>}
                </div>
              </div>
              <button type="submit" disabled={submittingVA} className="w-full py-3 bg-lojack-blue-600 hover:bg-lojack-blue-700 text-white rounded-xl font-medium transition-colors disabled:opacity-50 flex items-center justify-center gap-2" data-testid="va-form-submit">
                {submittingVA ? 'Salvataggio...' : 'Registra Assessment'}
              </button>
            </form>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {/* 1. Executive Summary */}
        <Section icon={Shield} title="Executive Summary" color="text-blue-400" bg="bg-blue-500/10">
          <p>La piattaforma <strong className="text-white">LoJack Digital Wallet</strong>, sviluppata da <strong className="text-white">TelcaVoIP AI</strong>, e progettata seguendo i principi di <strong className="text-white">Security by Design</strong> e <strong className="text-white">Privacy by Default</strong> in conformita con le normative europee.</p>
          <p>L'architettura adotta un approccio multi-layer alla sicurezza, implementando controlli a livello di rete, applicazione, dati e accesso, in linea con le raccomandazioni OWASP Top 10 e i requisiti del GDPR (Regolamento UE 2016/679).</p>
        </Section>

        {/* 2. Crittografia */}
        <Section icon={Lock} title="Crittografia e Protezione Dati" color="text-lojack-blue-400" bg="bg-lojack-blue-500/10">
          <Bullet><strong className="text-white">Comunicazioni HTTPS/TLS 1.3</strong> — Tutto il traffico tra client e server e cifrato con certificati SSL/TLS validi. Nessun dato transita in chiaro.</Bullet>
          <Bullet><strong className="text-white">Hashing Password (bcrypt)</strong> — Le password utente non sono mai memorizzate in chiaro. Vengono processate con l'algoritmo bcrypt con salt unico per ogni utente, rendendo computazionalmente impossibile il reverse engineering.</Bullet>
          <Bullet><strong className="text-white">Token JWT firmati</strong> — L'autenticazione utilizza JSON Web Token con firma crittografica e scadenza temporale configurabile. I token non contengono dati sensibili.</Bullet>
          <Bullet><strong className="text-white">Dati a riposo</strong> — Il database MongoDB utilizza crittografia a livello di storage (encryption at rest) su infrastruttura cloud certificata.</Bullet>
        </Section>

        {/* 3. Autenticazione */}
        <Section icon={Key} title="Autenticazione e Controllo Accessi" color="text-lojack-clause-400" bg="bg-lojack-clause-500/10">
          <Bullet><strong className="text-white">Autenticazione Multi-Fattore (MFA)</strong> — Il processo di firma digitale dei contratti utilizza OTP (One-Time Password) inviato via email, garantendo la verifica dell'identita del firmatario.</Bullet>
          <Bullet><strong className="text-white">Role-Based Access Control (RBAC)</strong> — Tre livelli di accesso segregati: Admin/SuperAdmin, Dealer, Venditore. Ogni ruolo ha permessi specifici e non puo accedere alle funzionalita degli altri ruoli.</Bullet>
          <Bullet><strong className="text-white">Sessioni sicure</strong> — Token con scadenza automatica (24h). Nessuna sessione persistente lato server. Logout esplicito invalida il token lato client.</Bullet>
          <Bullet><strong className="text-white">Protezione brute-force</strong> — Rate limiting sugli endpoint di autenticazione per prevenire attacchi a forza bruta.</Bullet>
        </Section>

        {/* 4. GDPR */}
        <Section icon={FileCheck} title="Conformita GDPR" color="text-purple-400" bg="bg-purple-500/10">
          <Bullet><strong className="text-white">Consenso esplicito</strong> — Prima della firma digitale, il cliente compila un form GDPR multilingue (18 lingue) con consensi granulari per marketing, comunicazioni commerciali e profilazione.</Bullet>
          <Bullet><strong className="text-white">Base giuridica documentata</strong> — Ogni trattamento dati e associato a una base giuridica specifica (consenso, esecuzione contratto, legittimo interesse).</Bullet>
          <Bullet><strong className="text-white">Diritto all'oblio</strong> — Possibilita di cancellare i dati del cliente su richiesta, inclusi contratti, card digitali e preferenze.</Bullet>
          <Bullet><strong className="text-white">Data minimization</strong> — Vengono raccolti solo i dati strettamente necessari all'erogazione del servizio.</Bullet>
          <Bullet><strong className="text-white">Registro dei trattamenti</strong> — Ogni operazione sui dati personali e tracciata con timestamp e identificativo operatore.</Bullet>
        </Section>

        {/* 5. Architettura */}
        <Section icon={Server} title="Architettura Sicura" color="text-cyan-400" bg="bg-cyan-500/10">
          <Bullet><strong className="text-white">Separazione frontend/backend</strong> — L'applicazione React (SPA) comunica con il backend FastAPI tramite API REST protette. Nessuna logica di business e esposta lato client.</Bullet>
          <Bullet><strong className="text-white">Input validation</strong> — Tutti gli input utente sono validati e sanitizzati sia lato client che lato server per prevenire injection attacks (SQL, NoSQL, XSS).</Bullet>
          <Bullet><strong className="text-white">CORS configurato</strong> — Cross-Origin Resource Sharing limitato ai domini autorizzati.</Bullet>
          <Bullet><strong className="text-white">Content Security Policy (CSP)</strong> — Header di sicurezza configurati per prevenire XSS, clickjacking e data injection.</Bullet>
          <Bullet><strong className="text-white">Infrastruttura cloud Kubernetes</strong> — Hosting su infrastruttura cloud con container Docker isolati, orchestrati tramite Kubernetes. Auto-scaling automatico, ridondanza geografica e isolamento dei workload. L'architettura containerizzata e portabile su qualsiasi provider cloud (AWS, Google Cloud, Azure) o datacenter on-premise, in base alle esigenze del cliente.</Bullet>
          <Bullet><strong className="text-white">Dominio dedicato</strong> — La piattaforma e raggiungibile tramite dominio dedicato con certificato SSL/TLS valido. Configurazione DNS personalizzabile (CNAME) per l'utilizzo di domini/sottodomini del cliente.</Bullet>
        </Section>

        {/* 6. OWASP */}
        <Section icon={AlertTriangle} title="Conformita OWASP Top 10" color="text-orange-400" bg="bg-orange-500/10">
          <p className="text-gray-400 mb-3">La piattaforma e sviluppata seguendo le linee guida OWASP (Open Web Application Security Project) per la mitigazione delle 10 vulnerabilita web piu critiche:</p>
          <div className="grid sm:grid-cols-2 gap-2">
            {[
              "A01 - Broken Access Control",
              "A02 - Cryptographic Failures",
              "A03 - Injection",
              "A04 - Insecure Design",
              "A05 - Security Misconfiguration",
              "A06 - Vulnerable Components",
              "A07 - Authentication Failures",
              "A08 - Data Integrity Failures",
              "A09 - Logging & Monitoring",
              "A10 - Server-Side Request Forgery",
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                <CheckCircle2 size={14} className="text-lojack-blue-400 flex-shrink-0" />
                <span className="text-gray-300">{item}</span>
              </div>
            ))}
          </div>
        </Section>

        {/* 7. Google Wallet */}
        <Section icon={Wifi} title="Integrazione Google Wallet" color="text-blue-400" bg="bg-blue-500/10">
          <Bullet><strong className="text-white">Approvazione Google</strong> — L'integrazione Google Wallet e stata approvata in produzione da Google, confermando la conformita agli standard di sicurezza richiesti da Google Pay & Wallet API.</Bullet>
          <Bullet><strong className="text-white">Service Account sicuro</strong> — L'accesso alle API Google avviene tramite Service Account con credenziali crittografate e permessi minimi necessari (principle of least privilege).</Bullet>
          <Bullet><strong className="text-white">JWT signed tokens</strong> — Le card digitali vengono generate con token firmati crittograficamente, impossibili da falsificare.</Bullet>
        </Section>

        {/* 8. Monitoraggio */}
        <Section icon={Eye} title="Monitoraggio e Incident Response" color="text-red-400" bg="bg-red-500/10">
          <Bullet><strong className="text-white">Logging applicativo</strong> — Tutti gli accessi, le operazioni critiche e gli errori vengono registrati con timestamp e contesto.</Bullet>
          <Bullet><strong className="text-white">Backup automatici</strong> — Il database MongoDB viene sottoposto a backup completi (dump) con frequenza configurabile (giornaliera/settimanale). Il sistema di backup include export automatico con download diretto dal pannello Admin e retention policy definita. I backup sono crittografati e possono essere archiviati su storage esterno sicuro.</Bullet>
          <Bullet><strong className="text-white">Notifiche in tempo reale</strong> — Sistema di notifiche push per eventi critici (nuovi clienti, firma contratti, anomalie).</Bullet>
          <Bullet><strong className="text-white">Disaster Recovery</strong> — Procedure di ripristino documentate con RTO (Recovery Time Objective) e RPO (Recovery Point Objective) definiti. Possibilita di rollback a checkpoint precedenti.</Bullet>
        </Section>

        {/* 8b. Sistema AI */}
        <Section icon={Database} title="Sistema AI e Gestione Dati" color="text-violet-400" bg="bg-violet-500/10">
          <Bullet><strong className="text-white">Modello AI utilizzato</strong> — La piattaforma integra Google Gemini tramite API autenticata e crittografata per funzionalita di supporto operativo e analisi. L'accesso avviene esclusivamente tramite chiavi API sicure con permessi minimi.</Bullet>
          <Bullet><strong className="text-white">Dati inviati al modello AI</strong> — Vengono trasmessi esclusivamente metriche aggregate e anonimizzate (conteggi, statistiche operative). Nessun dato personale identificabile (PII) dei clienti viene mai inviato a servizi AI esterni.</Bullet>
          <Bullet><strong className="text-white">AI Monitor locale</strong> — Il modulo di monitoraggio AI nella dashboard opera con calcoli algoritmici locali basati su dati di piattaforma, senza invio di informazioni a servizi terzi. Le metriche di ROI e performance sono calcolate internamente.</Bullet>
          <Bullet><strong className="text-white">Data segregation</strong> — I dati personali dei clienti (anagrafiche, contratti, firme digitali) risiedono esclusivamente nel database MongoDB crittografato e non vengono mai condivisi con modelli AI o servizi di terze parti.</Bullet>
          <Bullet><strong className="text-white">Conformita AI Act</strong> — L'utilizzo dell'AI e allineato ai principi del Regolamento europeo sull'Intelligenza Artificiale (AI Act - Reg. UE 2024/1689), con classificazione del rischio e documentazione delle finalita di trattamento.</Bullet>
        </Section>

        {/* 8c. Data Residency */}
        <Section icon={Globe} title="Data Residency e Conservazione Dati" color="text-lojack-blue-400" bg="bg-lojack-blue-500/10">
          <p className="mb-3">La piattaforma <strong className="text-white">LoJack Digital Wallet</strong> adotta una politica rigorosa di gestione, conservazione e localizzazione dei dati in conformita con il GDPR e la normativa europea.</p>

          <Bullet><strong className="text-white">Localizzazione dei server</strong> — Tutti i dati sono ospitati su infrastruttura cloud con data center situati nell'<strong className="text-white">Unione Europea</strong>. I server operano in conformita con le normative europee sulla protezione dei dati (GDPR - Reg. UE 2016/679).</Bullet>

          <Bullet><strong className="text-white">Nessun trasferimento extra-UE</strong> — I dati personali dei clienti non vengono mai trasferiti al di fuori dello Spazio Economico Europeo (SEE). Non sono in essere accordi di trasferimento dati verso paesi terzi.</Bullet>

          <Bullet><strong className="text-white">Tipologia dati conservati</strong> — La piattaforma registra e conserva le seguenti categorie di dati:</Bullet>
          <div className="ml-8 mt-1 mb-3 space-y-1.5">
            <p className="text-gray-400 text-sm flex items-start gap-2"><span className="text-lojack-blue-400 mt-1">&#8226;</span> <span><strong className="text-gray-300">Dati anagrafici</strong> — Nome, cognome, email, telefono, paese di residenza del cliente</span></p>
            <p className="text-gray-400 text-sm flex items-start gap-2"><span className="text-lojack-blue-400 mt-1">&#8226;</span> <span><strong className="text-gray-300">Dati veicolo</strong> — Marca, modello, targa, numero VIN, tipologia di servizio</span></p>
            <p className="text-gray-400 text-sm flex items-start gap-2"><span className="text-lojack-blue-400 mt-1">&#8226;</span> <span><strong className="text-gray-300">Contratti digitali</strong> — Documenti PDF firmati digitalmente tramite OTP, con timestamp e indirizzo IP della firma</span></p>
            <p className="text-gray-400 text-sm flex items-start gap-2"><span className="text-lojack-blue-400 mt-1">&#8226;</span> <span><strong className="text-gray-300">Preferenze GDPR</strong> — Consensi marketing, comunicazioni commerciali, profilazione, con data di registrazione</span></p>
            <p className="text-gray-400 text-sm flex items-start gap-2"><span className="text-lojack-blue-400 mt-1">&#8226;</span> <span><strong className="text-gray-300">Card digitali</strong> — Token Google Wallet, stato della card (creata, aggiunta al wallet)</span></p>
            <p className="text-gray-400 text-sm flex items-start gap-2"><span className="text-lojack-blue-400 mt-1">&#8226;</span> <span><strong className="text-gray-300">Credenziali di accesso</strong> — Email e password (hashate con bcrypt, mai in chiaro)</span></p>
            <p className="text-gray-400 text-sm flex items-start gap-2"><span className="text-lojack-blue-400 mt-1">&#8226;</span> <span><strong className="text-gray-300">Log operativi</strong> — Accessi, operazioni critiche, timestamp di ogni azione</span></p>
          </div>

          <Bullet><strong className="text-white">Retention policy (Durata conservazione)</strong> — I dati vengono conservati per la durata del rapporto contrattuale con il cliente e per un periodo massimo di <strong className="text-white">10 anni</strong> dalla cessazione del servizio, in conformita con gli obblighi fiscali e legali italiani (art. 2220 c.c.). I dati di log operativo sono conservati per <strong className="text-white">12 mesi</strong>. Al termine del periodo di conservazione, i dati vengono cancellati in modo sicuro e irreversibile.</Bullet>

          <Bullet><strong className="text-white">Backup e ridondanza</strong> — I backup del database vengono eseguiti con frequenza configurabile (giornaliera/settimanale), crittografati con algoritmo AES-256 e conservati in storage separato all'interno dell'UE. I backup sono soggetti alla stessa retention policy dei dati primari.</Bullet>

          <Bullet><strong className="text-white">Diritti dell'interessato</strong> — In conformita con gli artt. 15-22 del GDPR, gli interessati possono esercitare in qualsiasi momento i diritti di accesso, rettifica, cancellazione (diritto all'oblio), limitazione, portabilita e opposizione al trattamento dei propri dati, contattando il Titolare del trattamento.</Bullet>

          <Bullet><strong className="text-white">Titolare del trattamento</strong> — <strong className="text-white">TELCAVOIP INTERNATIONAL EU | A.I. DEPT.</strong> — info@telcavoip.eu — in qualita di responsabile della piattaforma e del trattamento dei dati per conto di LoJack Italia.</Bullet>
        </Section>

        {/* 9. Certificazioni */}
        <Section icon={ShieldCheck} title="Certificazioni Attive" color="text-lojack-blue-400" bg="bg-lojack-blue-500/10">
          <p className="mb-4">L'infrastruttura della piattaforma e l'organizzazione che la gestisce sono certificate secondo i piu' rigorosi standard internazionali. Le certificazioni <strong className="text-white">ISO/IEC 27001:2022</strong> e <strong className="text-white">ISO 9001</strong> sono riconosciute <strong className="text-white">IAF (International Accreditation Forum)</strong>.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            {[
              {
                code: "ISO/IEC 27001:2022",
                title: "Sicurezza delle Informazioni",
                cert_no: "060526054803",
                expiry: "05/05/2029",
                body: "ARS Assessment - UAF/IAF",
                file: "ISO27001_2022.pdf",
                color: "text-lojack-blue-400",
                border: "border-lojack-blue-500/30",
                iaf: true,
              },
              {
                code: "ISO 9001",
                title: "Sistema di Gestione Qualita'",
                cert_no: "120526014804",
                expiry: "11/05/2029",
                body: "ARS Assessment - UAF/IAF",
                file: "ISO9001.pdf",
                color: "text-lojack-clause-400",
                border: "border-lojack-clause-500/30",
                iaf: true,
              },
              {
                code: "ISO/IEC 27017",
                title: "Sicurezza dei Servizi Cloud",
                cert_no: "ITST/40AE/0526",
                expiry: "08/05/2029",
                body: "Quality Control Certification",
                file: "ISO27017.pdf",
                color: "text-blue-400",
                border: "border-blue-500/30",
                iaf: false,
              },
              {
                code: "ISO/IEC 27018",
                title: "Protezione PII nel Cloud Pubblico",
                cert_no: "PII/8858/0526",
                expiry: "08/05/2029",
                body: "Quality Control Certification - UASL UK",
                file: "ISO27018.pdf",
                color: "text-purple-400",
                border: "border-purple-500/30",
                iaf: false,
              },
            ].map((c) => (
              <a
                key={c.code}
                href={`${API}/assets/certifications/${c.file}`}
                target="_blank"
                rel="noopener noreferrer"
                className={`group bg-[#0D0D0D] border ${c.border} rounded-xl p-4 hover:bg-[#161616] transition-colors`}
                data-testid={`cert-${c.code.toLowerCase().replace(/[/:\s]+/g, "-")}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-lg bg-black/50 flex items-center justify-center flex-shrink-0`}>
                    <FileCheck size={18} className={c.color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <p className={`font-bold text-sm ${c.color}`}>{c.code}</p>
                        {c.iaf && (
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-lojack-blue-500/15 text-lojack-blue-300 border border-lojack-blue-500/30">IAF</span>
                        )}
                      </div>
                      <ExternalLink size={12} className="text-gray-500 group-hover:text-white transition-colors" />
                    </div>
                    <p className="text-white text-sm font-medium mt-0.5">{c.title}</p>
                    <p className="text-gray-400 text-xs mt-1.5">Cert. <span className="font-mono text-gray-300">{c.cert_no}</span></p>
                    <p className="text-gray-500 text-[11px] mt-0.5">Scadenza: {c.expiry}</p>
                    <p className="text-gray-600 text-[10px] mt-1 italic">{c.body}</p>
                  </div>
                </div>
              </a>
            ))}
          </div>
          <Bullet><strong className="text-white">Vulnerability Assessment</strong> — Eseguito periodicamente per garantire la sicurezza continua della piattaforma.</Bullet>
          <Bullet><strong className="text-white">Conformita NIS2</strong> — Allineamento alla Direttiva europea (UE) 2022/2555 sulla sicurezza delle reti e dei sistemi informativi.</Bullet>
          <Bullet><strong className="text-white">Aggiornamenti di sicurezza</strong> — Monitoraggio continuo delle CVE (Common Vulnerabilities and Exposures) e aggiornamento tempestivo delle dipendenze.</Bullet>
        </Section>

        {/* 10. Leadership & Profilo Professionale */}
        <Section icon={Star} title="Leadership & Profilo Professionale" color="text-indigo-400" bg="bg-indigo-500/10">
          <p className="mb-4">La piattaforma LoJack Digital Wallet e ideata e diretta da <strong className="text-white">Fabrizio Guerra</strong>, CEO di TelcaVoIP AI, professionista con un profilo di competenze riconosciuto a livello internazionale:</p>
          <div className="space-y-3 mb-4">
            <div className="flex items-start gap-3 bg-[#0D0D0D] border border-indigo-500/20 rounded-xl p-4" data-testid="profile-aifod">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                <Globe size={20} className="text-blue-400" />
              </div>
              <div>
                <p className="text-white font-medium text-sm">Membro Attivo AIFOD</p>
                <p className="text-gray-400 text-xs mt-1"><strong className="text-gray-300">AI for Developing Countries Forum</strong> (af.net) — La piu grande organizzazione mondiale dedicata all'AI per i paesi in via di sviluppo, con oltre 6.800 membri da 150+ paesi. Summit tenuti presso le sedi ONU di Vienna, Ginevra e Bangkok, con partecipazione di UNDP, UNICEF, WFP e rappresentanti governativi di tutto il mondo.</p>
                <a href="https://af.net/directory/fabrizio-guerra/" target="_blank" rel="noopener noreferrer" className="text-blue-400 text-xs hover:underline mt-1 inline-flex items-center gap-1">af.net/directory/fabrizio-guerra <ExternalLink size={10} /></a>
              </div>
            </div>
            <div className="flex items-start gap-3 bg-[#0D0D0D] border border-cyan-500/20 rounded-xl p-4" data-testid="profile-un-consultant">
              <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center flex-shrink-0">
                <Building2 size={20} className="text-cyan-400" />
              </div>
              <div>
                <p className="text-white font-medium text-sm">Consulente ONU in materia di Intelligenza Artificiale</p>
                <p className="text-gray-400 text-xs mt-1">Attivita di consulenza sui temi dell'AI etica, della sovranita digitale e dell'applicazione responsabile delle tecnologie di intelligenza artificiale in contesti internazionali.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 bg-[#0D0D0D] border border-lojack-clause-500/20 rounded-xl p-4" data-testid="profile-ier">
              <div className="w-10 h-10 rounded-lg bg-lojack-clause-500/10 flex items-center justify-center flex-shrink-0">
                <Award size={20} className="text-lojack-clause-400" />
              </div>
              <div>
                <p className="text-white font-medium text-sm">Registro Internazionale degli Esperti (IER)</p>
                <p className="text-gray-400 text-xs mt-1">Iscritto come <strong className="text-gray-300">Certified Global Expert</strong> in Intelligenza Artificiale con numero di serie <strong className="text-lojack-clause-400 font-mono">BF29277</strong>, certificazione accreditata AGCD e IHEAC (International Higher Education Accreditation Council).</p>
              </div>
            </div>
          </div>
        </Section>

        {/* 11. Team Certificazioni */}
        <Section icon={Award} title="Certificazioni Professionali" color="text-lojack-clause-400" bg="bg-lojack-clause-500/10">
          <p className="mb-4">Certificazioni professionali internazionali di <strong className="text-white">Fabrizio Guerra</strong>, CEO TelcaVoIP AI:</p>
          <div className="space-y-4">
            <CertCard
              title="Certified Global Expert — Artificial Intelligence (AI)"
              issuer="International Experts Register (IER) — AGCD / IHEAC Accredited"
              code="BF29277"
              url={`${API}/assets/certifications/ai_international_expert.jpg`}
            />
            <CertCard
              title="Certified Artificial Intelligence (AI) Expert"
              issuer="Youview London University — UK CPD Accredited"
              code="dcad97b"
              url={`${API}/assets/certifications/ai_certified_expert.pdf`}
            />
            <CertCard
              title="Certified Artificial Intelligence (AI) Trainer"
              issuer="Youview London University — AGCD Verified Platinum"
              code="081e79c"
              url={`${API}/assets/certifications/certified_trainer.pdf`}
            />
            <CertCard
              title="Certified Cyber Security Expert"
              issuer="Youview London University — UK CPD Accredited"
              code="30350c7"
              url={`${API}/assets/certifications/certified_cyber_security_expert.pdf`}
            />
            <CertCard
              title="IT Management — IT System Engineer"
              issuer="European Institute of Leadership & Management (EIILM), Dublin"
              code="14980-169-772-3413"
              url="/api/manuals/uploads/IT_Management_Certificate_Fixed.pdf"
            />
          </div>
        </Section>

        {/* Footer */}
        <div className="bg-[#0a1628] border border-gray-700 rounded-2xl p-6 sm:p-8 text-center">
          <img src={LOGO_URL} alt="TelcaVoIP Cyber Security" className="w-20 mx-auto mb-4 opacity-80" />
          <p className="text-gray-300 text-sm font-medium mb-1">TelcaVoIP AI — Divisione Cyber Security</p>
          <p className="text-gray-500 text-xs mb-4">Per informazioni sulla sicurezza e certificazioni:</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm">
            <a href="mailto:info@telcavoip.eu" className="text-blue-400 hover:text-blue-300 transition-colors">info@telcavoip.eu</a>
            <span className="text-gray-700 hidden sm:block">|</span>
            <a href="tel:+390427881500" className="text-blue-400 hover:text-blue-300 transition-colors">+39 0427 881500</a>
            <span className="text-gray-700 hidden sm:block">|</span>
            <a href="https://telcavoip.ai" target="_blank" rel="noreferrer" className="text-blue-400 hover:text-blue-300 transition-colors">telcavoip.ai</a>
          </div>
          <p className="text-gray-700 text-[10px] mt-4">Documento riservato — Rev. 1.0 — {currentDate}</p>
        </div>
      </div>
    </div>
  );
};

export default SecurityCompliancePage;
